import { Link } from "wouter";
import { ChevronRight } from "lucide-react";
import { PublicHeader } from "@/components/public-header";
import { PublicFooter } from "@/components/public-footer";
import { FeatureBlocks, FinalBand, FounderQuote, PageHero } from "@/components/marketing/sections";
import { useSEO } from "@/hooks/use-seo";
import { getCorePage, type CorePageSlug } from "@/data/core-pages";
import { seoFor } from "@/data/marketing-routes";

/**
 * The five core feature pages (build F6, Appendix A6):
 * /features/messages, /maintenance, /locks, /leasing, /numbers.
 */
export default function CoreFeaturePage({ slug }: { slug: CorePageSlug }) {
  const page = getCorePage(slug)!;
  useSEO(seoFor(page.path));

  return (
    <div className="flex min-h-screen flex-col bg-white" data-testid={`page-core-${slug}`}>
      <PublicHeader />
      <PageHero eyebrow={page.eyebrow} title={page.h1} sub={page.sub} ctaLocation={`core_${slug}`}>
        <nav className="mt-6 flex items-center justify-center gap-1.5 text-xs text-white/50" aria-label="Breadcrumb">
          <Link href="/" className="hover:text-white/80">
            Home
          </Link>
          <ChevronRight size={12} aria-hidden="true" />
          <Link href="/features" className="hover:text-white/80">
            Features
          </Link>
          <ChevronRight size={12} aria-hidden="true" />
          <span className="text-white/80">{page.name}</span>
        </nav>
      </PageHero>

      <main className="flex-grow">
        <section className="bg-gray-50 px-4 py-14 md:py-20">
          <FeatureBlocks blocks={page.blocks} />
          {page.proofLine && (
            <p className="mt-10 text-center text-lg font-semibold text-gray-900" data-testid="text-proof-line">
              {page.proofLine}
            </p>
          )}
        </section>

        <section className="px-4 py-14 md:py-20">
          <FounderQuote quote={page.quote} />
          {page.note && (
            <p className="mx-auto mt-10 max-w-2xl rounded-xl bg-blue-50 p-4 text-center text-sm text-gray-800" data-testid="text-core-note">
              {page.note}
            </p>
          )}
        </section>
      </main>

      <FinalBand location={`core_${slug}`} />
      <PublicFooter />
    </div>
  );
}
